import type { ComponentType } from 'preact';
import { render } from 'preact';
import { useEffect } from 'preact/hooks';
import { LocationProvider, Route, Router, useLocation } from 'preact-iso';
import { Spinner, Toasts } from './components/ui';
import { Invite, Login, Setup } from './routes/Auth';
import { Home } from './routes/Home';
import { Settings } from './routes/settings/Settings';
import { attempt, loadAuthState, page, user } from './state';
import './styles.css';

/** Pages that need a signed-in user; everyone else is sent to /login. */
function signedIn<P extends object>(Component: ComponentType<P>) {
  return function Guarded(props: P) {
    const { route } = useLocation();
    useEffect(() => {
      if (user.value === null) route('/login', true);
    }, [user.value]);
    return user.value ? <Component {...props} /> : <Spinner />;
  };
}

const GuardedHome = signedIn(Home);
const GuardedSettings = signedIn(Settings);

function SignedOutOnly({ component: Component }: { component: ComponentType }) {
  const { route } = useLocation();
  useEffect(() => {
    if (user.value) route('/', true);
  }, [user.value]);
  return <Component />;
}

const LoginPage = () => <SignedOutOnly component={Login} />;

function App() {
  useEffect(() => {
    void attempt(loadAuthState);
    const onUnauthenticated = () => {
      user.value = null;
      page.value = null;
    };
    window.addEventListener('flame:unauthenticated', onUnauthenticated);
    return () => window.removeEventListener('flame:unauthenticated', onUnauthenticated);
  }, []);

  if (user.value === undefined) return <Spinner />;
  return (
    <LocationProvider>
      <Router>
        <Route path="/login" component={LoginPage} />
        <Route path="/setup" component={Setup} />
        <Route path="/invite/:token" component={Invite} />
        <Route path="/settings/:tab?" component={GuardedSettings} />
        <Route default component={GuardedHome} />
      </Router>
      <Toasts />
    </LocationProvider>
  );
}

render(<App />, document.getElementById('app')!);
